/**
 * Syncs generated CPT table rows with order updates and optional UserStore cache.
 * @module useSyncTableDataWithOrder
 */
import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { isEqual } from "lodash";
import { useEffect } from "react";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { generateCptCodesTableData } from "../cptCodesTable";

/**
 * Parameters for {@link useSyncTableDataWithOrder}.
 */
interface UseSyncTableDataWithOrderParams {
  generalOrderDetails?: GeneralOrderDetails;
  authOnFileDetails?: AuthOnFile;
  orderId?: string;
  authStatusOptions: AuthStatusOptionModel[];
  getCptCodes: (orderId: string) => any;
  setTableData: React.Dispatch<React.SetStateAction<CptCodesTableRowModel[]>>;
  setOriginalTableData: React.Dispatch<
    React.SetStateAction<CptCodesTableRowModel[]>
  >;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  saveTableDataToContext: (data: CptCodesTableRowModel[]) => void;
  /**
   * Options surfaced inside each SEARCHABLE_CARD description cell, forwarded
   * to {@link generateCptCodesTableData}.
   */
  options: SearchableCardOption[];
  /** Healthcare facility `category` driving the start-date validation exemption. */
  category?: string | null;
}

/**
 * Keeps the CPT codes table in sync with the live Firestore order data.
 *
 * When `singleMedicalPaOrder` updates (via the real-time listener), this hook
 * regenerates the table data and reconciles it with any data persisted in the
 * UserStore context. If both are identical the context copy is reused; otherwise
 * the freshly generated data wins and the context is updated to match.
 */
export const useSyncTableDataWithOrder = ({
  generalOrderDetails,
  authOnFileDetails,
  orderId,
  authStatusOptions,
  getCptCodes,
  setTableData,
  setOriginalTableData,
  setHasChanges,
  saveTableDataToContext,
  options,
  category,
}: UseSyncTableDataWithOrderParams) => {
  useEffect(() => {
    if (!generalOrderDetails) return;

    const generatedData = generateCptCodesTableData(
      generalOrderDetails,
      authOnFileDetails ?? {},
      authStatusOptions ?? [],
      options ?? [],
      category,
    );

    const dataFromContext = getCptCodes(orderId ?? "");

    if (
      dataFromContext &&
      Array.isArray(dataFromContext) &&
      dataFromContext.length === generatedData.length &&
      isEqual(dataFromContext, generatedData)
    ) {
      setTableData(dataFromContext as CptCodesTableRowModel[]);
      setHasChanges(false);
      setOriginalTableData(generatedData as CptCodesTableRowModel[]);
    } else {
      setTableData(generatedData as CptCodesTableRowModel[]);
      setOriginalTableData(generatedData as CptCodesTableRowModel[]);
      saveTableDataToContext(generatedData as CptCodesTableRowModel[]);
      setHasChanges(false);
    }
  }, [
    generalOrderDetails,
    authOnFileDetails,
    orderId,
    authStatusOptions,
    options,
    category,
    getCptCodes,
    setTableData,
    setOriginalTableData,
    setHasChanges,
    saveTableDataToContext,
  ]);
};
